'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA } from '@/app/components/blog/BlogAeo'
import RiskQuizInlineNudge from '@/app/components/blog/RiskQuizInlineNudge'
import KeyStats from '@/app/components/blog/KeyStats'
import CorrectionNote from '@/app/components/blog/CorrectionNote'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Does PUR Gum Have Microplastics? The Gum Base Question (2026)"
      author="MicroPlastics Research Desk"
      date="July 24, 2026"
      readTime="9 min read"
      heroImage="/blog/heroes/food.webp"
      heroImageAlt="Does PUR gum have microplastics: the aspartame-free gum audited on its undisclosed gum base against the plastic-free options"
    >
      <BlogLastUpdated iso="2026-10-18" formatted="October 18, 2026" />

      <CorrectionNote date="October 18, 2026">
        The UCLA gum study is now peer-reviewed (Lowe, Leonard and Mohanty, Journal of Hazardous Materials Letters, 2025), and we replaced a broken press-release link. We also removed the claim that PUR &ldquo;falls on the higher-shedding side&rdquo;: the study found natural and synthetic gums released statistically similar numbers of particles, and it did not name PUR.
      </CorrectionNote>

      <BlogTLDR>
        <strong>Almost certainly, because &ldquo;gum base&rdquo; is the tell,
        and PUR doesn&apos;t disclose a chicle one.</strong>{' '}
        PUR built its brand on being <strong>aspartame-free, sugar-free, and
        vegan</strong>, all of which are real and separate from the plastics
        question. What matters for microplastics is the <strong>gum
        base</strong>: the chewy part. When a label just says &ldquo;gum
        base&rdquo; without naming <strong>chicle</strong> (natural tree sap),
        it is almost always a synthetic blend of food-grade polymers, the same
        plastics family, polyethylene, polyvinyl acetate, and styrene-butadiene
        rubber, that a 2025 peer-reviewed UCLA study found sheds hundreds of
        microplastic particles per piece while you chew. PUR does not market a
        chicle base, so treat it like conventional gum on this axis. If a
        plastic-free chew is what you want, the tell is the ingredient list:{' '}
        <strong>look for &ldquo;chicle&rdquo; by name</strong>, as with{' '}
        <Link href="/blog/does-simply-gum-have-microplastics" className="text-forest underline">Simply Gum</Link>.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>PUR&apos;s selling points, <strong>aspartame-free, sugar-free, vegan, non-GMO</strong>, are real but say nothing about the plastic gum base.</>,
          <>The microplastic question in any gum is the <strong>gum base</strong>. Undisclosed &ldquo;gum base&rdquo; is almost always synthetic (plastic).</>,
          <>PUR does <strong>not market a chicle (natural) base</strong>, so on microplastics it behaves like conventional gum.</>,
          <>A 2025 peer-reviewed UCLA study found chewing gum can shed <strong>hundreds of microplastic particles per piece</strong> into saliva, most in the first two minutes.</>,
          <>No PUR-specific particle count has been published. Any exact figure attributed to PUR is fabricated.</>,
          <>The only reliably plastic-free chew names <strong>chicle</strong> on the label, like <Link href="/blog/does-simply-gum-have-microplastics" className="text-forest underline">Simply Gum</Link>. See the full <Link href="/blog/chewing-gum-brands-ranked-microplastics" className="text-forest underline">gum brands ranked</Link>.</>,
        ]}
      />

      <RiskQuizInlineNudge source="blog_inline_pur_gum" />

      <KeyStats
        heading="PUR gum and microplastics: what the research supports"
        stats={[
          { value: 'Gum base', label: 'the part that matters for plastics', context: 'undisclosed gum base is almost always synthetic polymer', source: 'gum composition analysis', sourceUrl: 'https://microplasticsapp.com/blog/chewing-gum-brands-ranked-microplastics' },
          { value: 'Hundreds', label: 'microplastic particles per piece of gum', context: 'shed into saliva while chewing, most in the first 2 minutes', source: 'Lowe, Leonard & Mohanty, J Hazard Mater Lett 2025', sourceUrl: 'https://doi.org/10.1016/j.hazl.2025.100164' },
          { value: 'No chicle', label: 'PUR does not market a natural base', context: 'so it behaves like conventional synthetic-base gum here', source: 'PUR product labeling', sourceUrl: 'https://purcompany.com/' },
          { value: 'Not tested', label: 'PUR-specific published count', context: 'no lab has measured PUR gum directly', source: 'no published study', sourceUrl: 'https://microplasticsapp.com/blog/chewing-gum-brands-ranked-microplastics' },
        ]}
      />

      <h2>Does PUR gum contain microplastics?</h2>
      <p>
        Most likely, yes, but not for the reasons PUR&apos;s marketing addresses.
        PUR is genuinely aspartame-free, sugar-free, and vegan, and if those are
        your concerns, it delivers. None of them touch the microplastics
        question, which comes down to a single ingredient: the{' '}
        <strong>gum base</strong>, the insoluble chewy matrix that gives gum its
        texture. You chew it, you don&apos;t swallow it, and the whole time it
        is in contact with warm saliva and mechanical force.
      </p>
      <p>
        Here is the labeling trick that decides the answer. Under US rules, a
        manufacturer can list &ldquo;gum base&rdquo; as a single ingredient
        without disclosing what is in it, and the base is typically a proprietary
        blend of food-grade synthetic polymers: polyethylene, polyvinyl acetate,
        and styrene-butadiene rubber, among others. Those are plastics. A gum
        that uses a <em>natural</em> base instead will almost always say so
        proudly, because it is a selling point, by naming <strong>chicle</strong>
        (chicle tree sap) on the ingredient list. PUR lists &ldquo;gum
        base&rdquo; and does not market a chicle base, which puts it in the
        synthetic-base category for microplastics.
      </p>

      <h2>Why the 2025 gum study matters here</h2>
      <p>
        The reason this is more than theoretical: a 2025 study from UCLA
        researchers, since peer-reviewed in the Journal of Hazardous Materials
        Letters, found that chewing a single piece of gum released{' '}
        <strong>hundreds of microplastic particles</strong> into saliva, with the
        largest burst in the first couple of minutes of chewing. The study looked
        at both synthetic-base and natural-base gums, without naming brands, and
        found the two groups released statistically similar numbers of
        particles (4 to 636 per gram overall). PUR was not tested by name.
        Because PUR uses an undisclosed gum base rather than a named chicle
        base, treat it like any conventional gum until PUR discloses more.
      </p>

      <h2>PUR vs the alternatives</h2>
      <BlogComparisonTable
        caption="PUR gum vs other options, by gum-base type"
        columns={[
          { key: 'brand', label: 'Gum' },
          { key: 'base', label: 'Gum base' },
          { key: 'plastic', label: 'Microplastic risk' },
        ]}
        rows={[
          { brand: 'Simply Gum', base: 'Chicle (named on label)', plastic: 'Lowest' },
          { brand: 'Other chicle-based gums', base: 'Chicle (named)', plastic: 'Lowest' },
          { brand: 'PUR', base: '"Gum base" (undisclosed)', plastic: 'Assume synthetic' },
          { brand: 'Conventional gum (Trident, Orbit, Extra)', base: 'Synthetic polymer', plastic: 'Higher' },
        ]}
      />
      <p>
        PUR is a cleaner choice than conventional gum on sweeteners and
        additives, but on the plastic base it is in the same undisclosed-synthetic
        bucket until the label says chicle. For the brand-by-brand picture, see
        our <Link href="/blog/chewing-gum-brands-ranked-microplastics" className="text-forest underline">chewing gum brands ranked</Link>{' '}
        guide, and compare against{' '}
        <Link href="/blog/does-simply-gum-have-microplastics" className="text-forest underline">Simply Gum</Link>{' '}
        and{' '}
        <Link href="/blog/does-trident-have-microplastics" className="text-forest underline">Trident</Link>.
      </p>

      <h2>What to chew instead</h2>
      <ul>
        <li>
          <strong>Read for &ldquo;chicle.&rdquo;</strong> The single reliable
          signal of a plastic-free gum is the word chicle on the ingredient
          list. If it just says &ldquo;gum base,&rdquo; assume synthetic.
        </li>
        <li>
          <strong>Treat the first two minutes as the exposure.</strong> The UCLA
          study found most shedding happens early; chewing longer doesn&apos;t
          add much, but starting with a plastic base is the variable you control.
        </li>
        <li>
          <strong>Consider mints or nothing.</strong> For fresh breath without a
          plastic base, a mint that dissolves leaves no chewed polymer behind.
        </li>
      </ul>

      <BlogAppCTA
        headline="Scan the gum in your bag"
        sub="Point the MicroPlastics app at PUR, Simply Gum, or any brand to see whether the base is synthetic and what a plastic-free swap looks like."
      />

      <BlogFAQ
        items={[
          {
            question: 'Is PUR gum plastic-free?',
            answer: 'PUR does not market a chicle (natural) base and lists "gum base" as an undisclosed ingredient, which is almost always a synthetic polymer blend. Unless PUR states a chicle base, it should be treated as a synthetic-base gum for microplastics. PUR is aspartame-free and sugar-free, but that is a separate question from the plastic base.',
          },
          {
            question: 'What is PUR gum base made of?',
            answer: 'PUR does not publicly specify. US labeling lets manufacturers list "gum base" without disclosing the blend, which is typically food-grade synthetic polymers such as polyethylene, polyvinyl acetate, and styrene-butadiene rubber. A natural-base gum would name chicle instead, which PUR does not.',
          },
          {
            question: 'Is PUR gum better than Trident or Orbit?',
            answer: 'On sweeteners and additives, yes, PUR avoids aspartame and sugar. On the plastic gum base, there is no clear advantage: all of them use an undisclosed or synthetic base rather than named chicle. For a plastic-free chew, choose a gum that lists chicle, such as Simply Gum.',
          },
          {
            question: 'Does chewing gum really shed microplastics?',
            answer: 'Yes, based on a 2025 peer-reviewed UCLA study that found a single piece of gum released hundreds of microplastic particles into saliva, most within the first two minutes of chewing. Synthetic-base gums are the ones built from plastic polymers; chicle-based gums are the low-shedding alternative.',
          },
        ]}
      />

      <BlogSources
        items={[
          { authors: 'Lowe L, Leonard J, Mohanty S (UCLA)', year: 2025, title: 'Ingestion of microplastics during chewing gum consumption (peer-reviewed; first presented as a pilot at ACS Spring 2025)', publisher: 'Journal of Hazardous Materials Letters 6:100164', url: 'https://doi.org/10.1016/j.hazl.2025.100164' },
          { authors: 'PUR Company', year: 2026, title: 'PUR gum ingredients and product information', publisher: 'purcompany.com', url: 'https://purcompany.com/' },
          { authors: 'Various', year: 2025, title: 'Effects of Microplastic Exposure on Human Health: A Rapid Systematic Review', publisher: 'PMC / NCBI', url: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC11697325/' },
        ]}
      />
    </BlogPostLayout>
  )
}
